/**
 * ArcGIS-style swipe tool: while swipe mode is on, dragging on the map reveals
 * the basemap beneath the selected event layers along the drag axis, and the
 * split KEEPS its position when the mouse is released (e.g. for screenshots of
 * a half-swiped map). A divider line marks where the layers are cut; it can be
 * grabbed and dragged to slide the split, or a new drag on the map re-orients
 * it. Turning swipe mode off removes the split.
 *
 * The split is ANCHORED TO THE MAP: `swipeAtom.coord` stores a longitude
 * (horizontal axis) or latitude (vertical axis), not screen pixels, so panning
 * or zooming never moves the split relative to the data. Cursor positions are
 * unprojected to geo at input time; the divider projects the coord back to
 * screen every render so it tracks the map.
 *
 * The map drag is captured with CAPTURE-phase pointer listeners on the map
 * container (no blocking overlay, so the scroll wheel still reaches deck.gl
 * and zooms). Mouse buttons are routed by intent:
 *   - LEFT drag  -> swipe (clip the selected event layers along the drag axis).
 *   - MIDDLE drag -> pan the map (deck's drag-pan is disabled in swipe mode, so
 *                    we pan manually by writing the view state).
 *   - RIGHT      -> ignored (no swipe; Map.tsx keeps the browser's context menu
 *                   off the map surface regardless of swipe mode).
 * Deck's left-drag pan / right-drag rotate are turned off in Map.tsx while
 * swipe mode is on, so those buttons are free; scroll-zoom stays enabled.
 */
import React from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { WebMercatorViewport } from '@deck.gl/core';
import { swipeAtom, swipeEnabledAtom } from './atoms';
import { viewStateAtom } from '../atoms';
import type { ViewStateProps } from '../deckgl-types';
import { DECK_WRAPPER_SELECTOR } from '../deckWrapper';
import { ACCENT, DARK_BLUE, WHITE } from './palette';

type Props = {
  /** The map container element -- drag coordinates are taken relative to it. */
  containerRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Movement (px) the cursor must travel before the swipe commits to an axis.
 * Until this is exceeded no clip is applied; once exceeded the axis (and
 * direction) are LOCKED for the rest of the drag so diagonal wobble can't flip
 * them.
 */
const AXIS_LOCK_THRESHOLD = 8;

const LEFT_BUTTON = 0;
const MIDDLE_BUTTON = 1;

/** Width of the invisible strip around the divider line that accepts a grab,
 * so the line is draggable without pixel-perfect aim. */
const GRAB_STRIP_WIDTH = 14;

/** How far (px) an arrow key moves the divider; Shift takes bigger steps. */
const KEYBOARD_STEP_PX = 2;
const KEYBOARD_STEP_SHIFT_PX = 20;

type Pt = { x: number; y: number };
type Axis = 'horizontal' | 'vertical';

/** Lock axis + direction from a drag delta: axis = dominant of |dx|,|dy|,
 * dir = sign along it. */
function lockAxisDir(dx: number, dy: number): { axis: Axis; dir: 1 | -1 } {
  if (Math.abs(dx) >= Math.abs(dy)) {
    return { axis: 'horizontal', dir: dx >= 0 ? 1 : -1 };
  }
  return { axis: 'vertical', dir: dy >= 0 ? 1 : -1 };
}

/** Unproject a container-relative cursor position to the split's geo anchor:
 * longitude at the cursor x (horizontal axis) or latitude at the cursor y
 * (vertical). Returns null when the unprojection is unusable. */
function cursorToCoord(
  axis: Axis,
  p: Pt,
  size: { w: number; h: number },
  viewState: ViewStateProps
): number | null {
  if (size.w <= 0 || size.h <= 0) return null;
  const vp = new WebMercatorViewport({ ...viewState, width: size.w, height: size.h });
  const coord =
    axis === 'horizontal' ? vp.unproject([p.x, size.h / 2])[0] : vp.unproject([size.w / 2, p.y])[1];
  return Number.isFinite(coord) ? coord : null;
}

export const SwipeInteraction: React.FC<Props> = ({ containerRef }) => {
  const enabled = useAtomValue(swipeEnabledAtom);
  const [swipe, setSwipe] = useAtom(swipeAtom);
  const setViewState = useSetAtom(viewStateAtom);

  // Keep the latest view state in a ref so the listeners (attached once per
  // enable) read fresh values without re-subscribing on every pan tick.
  // Written in an effect: a ref write during render leaks out of discarded
  // concurrent renders.
  const viewState = useAtomValue(viewStateAtom);
  const viewStateRef = React.useRef(viewState);
  React.useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

  const modeRef = React.useRef<'swipe' | 'pan' | null>(null);
  const startRef = React.useRef<Pt | null>(null);
  const axisRef = React.useRef<Axis | null>(null);
  const dirRef = React.useRef<1 | -1>(1);
  const panStartRef = React.useRef<{ p: Pt; vs: ViewStateProps } | null>(null);
  // Swipe updates are coalesced to one atom write per frame: every write
  // re-runs the layer overrides over the whole flattened layer tree, which is
  // far too much work to do at raw pointer-event rate.
  const pendingPointRef = React.useRef<(Pt & { w: number; h: number }) | null>(null);
  const rafRef = React.useRef<number | null>(null);

  // The container's pixel size, measured into state rather than read off the
  // ref while rendering: the divider is laid out in container pixels, and a
  // render-phase ref read is not re-run when the element resizes.
  const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 });
  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (container == null) return;
    const measure = () =>
      setContainerSize(previous =>
        previous.width === container.clientWidth && previous.height === container.clientHeight
          ? previous
          : { width: container.clientWidth, height: container.clientHeight }
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef]);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return;

    const relative = (clientX: number, clientY: number): Pt & { w: number; h: number } => {
      const r = el.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top, w: r.width, h: r.height };
    };

    const onPointerDown = (e: PointerEvent) => {
      // Only engage on the actual map surface. The control panels (layers
      // panel, swipe toggle/chips, nav buttons) and the divider itself are
      // siblings of the deck wrapper inside #map-container; without this guard
      // the capture-phase listener would hijack their pointer events while
      // swipe is on.
      const target = e.target as Element | null;
      if (!target?.closest(DECK_WRAPPER_SELECTOR)) return;

      if (e.button === LEFT_BUTTON) {
        modeRef.current = 'swipe';
        startRef.current = relative(e.clientX, e.clientY);
        axisRef.current = null;
        el.setPointerCapture?.(e.pointerId);
        e.preventDefault();
      } else if (e.button === MIDDLE_BUTTON) {
        modeRef.current = 'pan';
        panStartRef.current = {
          p: relative(e.clientX, e.clientY),
          vs: { ...viewStateRef.current },
        };
        el.setPointerCapture?.(e.pointerId);
        e.preventDefault();
      } else {
        // Right / other buttons: never swipe.
        modeRef.current = null;
      }
    };

    const flushSwipe = () => {
      rafRef.current = null;
      const p = pendingPointRef.current;
      const axis = axisRef.current;
      if (p == null || axis == null) return;
      const coord = cursorToCoord(axis, p, { w: p.w, h: p.h }, viewStateRef.current);
      if (coord != null) setSwipe({ axis, coord, dir: dirRef.current });
    };

    const onPointerMove = (e: PointerEvent) => {
      if (modeRef.current === 'swipe') {
        const start = startRef.current;
        if (!start) return;
        const p = relative(e.clientX, e.clientY);
        const dx = p.x - start.x;
        const dy = p.y - start.y;
        if (axisRef.current == null) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_THRESHOLD) return;
          const locked = lockAxisDir(dx, dy);
          axisRef.current = locked.axis;
          dirRef.current = locked.dir;
        }
        pendingPointRef.current = p;
        rafRef.current ??= requestAnimationFrame(flushSwipe);
      } else if (modeRef.current === 'pan') {
        const panStart = panStartRef.current;
        if (!panStart) return;
        const cur = relative(e.clientX, e.clientY);
        const vs0 = panStart.vs;
        const vp = new WebMercatorViewport({ ...vs0, width: cur.w, height: cur.h });
        const from = vp.unproject([panStart.p.x, panStart.p.y]);
        const to = vp.unproject([cur.x, cur.y]);
        // Only the camera position changes; a transition left on the atom by
        // a fly-to must not replay on every pan tick.
        setViewState({
          ...vs0,
          longitude: vs0.longitude + (from[0] - to[0]),
          latitude: vs0.latitude + (from[1] - to[1]),
          transitionDuration: undefined,
          transitionInterpolator: undefined,
        });
      }
    };

    const endDrag = (e: PointerEvent) => {
      if (modeRef.current == null) return;
      // The clip stays where it was released; only disabling swipe clears it.
      // Flush any coalesced move first so the split lands exactly where the
      // cursor was released.
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        flushSwipe();
      }
      modeRef.current = null;
      startRef.current = null;
      axisRef.current = null;
      pendingPointRef.current = null;
      panStartRef.current = null;
      if (el.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
    };

    const opts = { capture: true } as const;
    el.addEventListener('pointerdown', onPointerDown, opts);
    el.addEventListener('pointermove', onPointerMove, opts);
    el.addEventListener('pointerup', endDrag, opts);
    el.addEventListener('pointercancel', endDrag, opts);
    // (The swipe crosshair is shown via deck's getCursor prop in Map.tsx --
    // deck manages its canvas cursor, which overrides any container style.)

    return () => {
      el.removeEventListener('pointerdown', onPointerDown, opts);
      el.removeEventListener('pointermove', onPointerMove, opts);
      el.removeEventListener('pointerup', endDrag, opts);
      el.removeEventListener('pointercancel', endDrag, opts);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      pendingPointRef.current = null;
      modeRef.current = null;
      setSwipe(null);
    };
  }, [enabled, containerRef, setSwipe, setViewState]);

  // Dragging the divider slides the split along its existing axis (a drag on
  // the map is what re-orients it), so the grab is a plain 1-D adjustment.
  const gripDraggingRef = React.useRef(false);

  const onGripPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== LEFT_BUTTON) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    gripDraggingRef.current = true;
    e.preventDefault();
  };

  const onGripPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    if (!gripDraggingRef.current || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = {
      x: Math.min(Math.max(e.clientX - r.left, 0), r.width),
      y: Math.min(Math.max(e.clientY - r.top, 0), r.height),
    };
    setSwipe(prev => {
      if (prev == null) return prev;
      const coord = cursorToCoord(prev.axis, p, { w: r.width, h: r.height }, viewStateRef.current);
      return coord == null ? prev : { ...prev, coord };
    });
  };

  const onGripPointerUp = () => {
    gripDraggingRef.current = false;
  };

  // Keyboard path for the slider role: arrow keys along the split's axis step
  // the divider, Shift steps further. The step is applied in screen px and
  // unprojected, matching what a small drag does.
  const onGripKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const el = containerRef.current;
    if (!el || swipe == null) return;
    const forwardKey = swipe.axis === 'horizontal' ? 'ArrowRight' : 'ArrowDown';
    const backKey = swipe.axis === 'horizontal' ? 'ArrowLeft' : 'ArrowUp';
    if (e.key !== forwardKey && e.key !== backKey) return;
    e.preventDefault();
    const step =
      (e.shiftKey ? KEYBOARD_STEP_SHIFT_PX : KEYBOARD_STEP_PX) * (e.key === forwardKey ? 1 : -1);
    const r = el.getBoundingClientRect();
    const vp = new WebMercatorViewport({ ...viewState, width: r.width, height: r.height });
    const pos =
      swipe.axis === 'horizontal'
        ? vp.project([swipe.coord, viewState.latitude])[0]
        : vp.project([viewState.longitude, swipe.coord])[1];
    const axisExtent = swipe.axis === 'horizontal' ? r.width : r.height;
    const next = Math.min(Math.max(pos + step, 0), axisExtent);
    const p =
      swipe.axis === 'horizontal' ? { x: next, y: r.height / 2 } : { x: r.width / 2, y: next };
    const coord = cursorToCoord(swipe.axis, p, { w: r.width, h: r.height }, viewState);
    if (coord != null) setSwipe({ ...swipe, coord });
  };

  if (!enabled || swipe == null) return null;

  // Project the geo anchor back to container px each render, so the divider
  // tracks the map as it pans/zooms (the clip is geo-anchored and never moves).
  const { width, height } = containerSize;
  if (width <= 0 || height <= 0) return null;
  const vp = new WebMercatorViewport({ ...viewState, width, height });
  const linePos =
    swipe.axis === 'horizontal'
      ? vp.project([swipe.coord, viewState.latitude])[0]
      : vp.project([viewState.longitude, swipe.coord])[1];
  if (!Number.isFinite(linePos)) return null;

  const extent = swipe.axis === 'horizontal' ? width : height;
  // When the split is panned out of view the line disappears, but the grab
  // strip clamps to the map edge so the split always stays reachable.
  const lineVisible = linePos >= 0 && linePos <= extent;
  const stripPos = Math.min(Math.max(linePos, 0), extent);

  // axis 'horizontal' = a VERTICAL divider line moved left/right.
  const verticalLine = swipe.axis === 'horizontal';

  return (
    <Box
      role="slider"
      aria-label="Swipe divider position"
      aria-orientation={verticalLine ? 'horizontal' : 'vertical'}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round((stripPos / extent) * 100)}
      aria-valuetext={`Divider at ${Math.round((stripPos / extent) * 100)}% from the ${
        verticalLine ? 'left' : 'top'
      } of the map`}
      tabIndex={0}
      onKeyDown={onGripKeyDown}
      onPointerDown={onGripPointerDown}
      onPointerMove={onGripPointerMove}
      onPointerUp={onGripPointerUp}
      onPointerCancel={onGripPointerUp}
      sx={{
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '-2px' },
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: verticalLine ? 'ew-resize' : 'ns-resize',
        touchAction: 'none',
        pointerEvents: 'auto',
        // The divider belongs to the map, not the UI: zIndex 0 paints above
        // the deck wrapper (also z 0, but earlier in the DOM) yet below every
        // overlaid panel -- the lowest panel z-index in the container is the
        // Stats window at 1. A grip parked behind a panel is retrieved by
        // panning the map (the divider moves with it) or closing the panel.
        zIndex: 0,
        ...(verticalLine
          ? {
              top: 0,
              bottom: 0,
              left: stripPos - GRAB_STRIP_WIDTH / 2,
              width: GRAB_STRIP_WIDTH,
            }
          : {
              left: 0,
              right: 0,
              top: stripPos - GRAB_STRIP_WIDTH / 2,
              height: GRAB_STRIP_WIDTH,
            }),
      }}
    >
      {lineVisible && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            backgroundColor: WHITE,
            boxShadow: '0 0 4px rgba(0,0,0,0.6)',
            ...(verticalLine
              ? { top: 0, bottom: 0, left: '50%', width: '2px', transform: 'translateX(-50%)' }
              : { left: 0, right: 0, top: '50%', height: '2px', transform: 'translateY(-50%)' }),
          }}
        />
      )}
      {/* The grab affordance: a slim grip pill riding ON the line (drag
          ridges, resize cursor) rather than a floating circular button, so it
          reads as "slide the divider", not "click me". */}
      <Box
        aria-hidden
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: WHITE,
          borderRadius: '7px',
          boxShadow: '0 0 6px rgba(0,0,0,0.6)',
          color: DARK_BLUE,
          ...(verticalLine
            ? { width: GRAB_STRIP_WIDTH, height: 44 }
            : { width: 44, height: GRAB_STRIP_WIDTH }),
        }}
      >
        <DragIndicatorIcon
          sx={{
            fontSize: 18,
            ...(verticalLine ? {} : { transform: 'rotate(90deg)' }),
          }}
        />
      </Box>
    </Box>
  );
};
